/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Scheduled Jobs Dashboard Page
 * ============================================================================
 *
 * Dashboard for monitoring and managing scheduled background jobs.
 * Shows job status, execution history, and provides control actions.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Timer,
  Calendar,
  Activity,
} from 'lucide-react';
import {
  useJobs,
  useJobDashboardOverview,
  useTriggerJob,
  usePauseJob,
  useResumeJob,
} from '@/hooks/api/useJobs';
import { ScheduledJob, ExecutionStatus } from '@/services/superadmin/jobs.service';
import { formatDistanceToNow } from 'date-fns';

export function JobDashboardPage() {
  const { t } = useTranslation('platform');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);

  const { data: jobs, isLoading, error, refetch } = useJobs();
  const { data: overview } = useJobDashboardOverview();
  const triggerMutation = useTriggerJob();
  const pauseMutation = usePauseJob();
  const resumeMutation = useResumeJob();

  const getStatusIcon = (status: ExecutionStatus | null) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="status-icon status-success" />;
      case 'FAILED':
        return <XCircle className="status-icon status-failed" />;
      case 'RUNNING':
        return <RefreshCw className="status-icon status-running animate-spin" />;
      case 'SKIPPED':
        return <AlertTriangle className="status-icon status-skipped" />;
      default:
        return <Clock className="status-icon status-idle" />;
    }
  };

  const handleTrigger = (jobName: string) => {
    triggerMutation.mutate(jobName);
  };

  const handlePause = (jobName: string) => {
    pauseMutation.mutate(jobName);
  };

  const handleResume = (jobName: string) => {
    resumeMutation.mutate(jobName);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <RefreshCw className="animate-spin" size={24} />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <AlertTriangle size={24} />
          <span>{t('common.error')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Clock className="title-icon" />
            {t('jobs.title')}
          </h1>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => refetch()}>
              <RefreshCw size={16} />
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Calendar size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.totalJobs}</span>
              <span className="stat-label">{t('jobs.stats.totalJobs')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon active">
              <Activity size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.activeJobs}</span>
              <span className="stat-label">{t('jobs.stats.activeJobs')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon paused">
              <Pause size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.pausedJobs}</span>
              <span className="stat-label">{t('jobs.stats.pausedJobs')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Timer size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.executionsToday}</span>
              <span className="stat-label">{t('jobs.stats.executionsToday')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon error">
              <XCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value error-value">{overview.failuresToday}</span>
              <span className="stat-label">{t('jobs.stats.failuresToday')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <CheckCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value success-value">{overview.successRate}%</span>
              <span className="stat-label">{t('jobs.stats.successRate')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      <div className="section">
        <h2 className="section-title">{t('jobs.allJobs')}</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('jobs.columns.name')}</th>
                <th>{t('jobs.columns.schedule')}</th>
                <th>{t('jobs.columns.lastRun')}</th>
                <th>{t('jobs.columns.stats')}</th>
                <th>{t('jobs.columns.status')}</th>
                <th>{t('jobs.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {jobs?.map((job) => (
                <tr key={job.id} className={job.paused ? 'row-paused' : ''}>
                  <td>
                    <div className="job-name-cell">
                      <span className="job-name">{job.jobName}</span>
                      {job.description && (
                        <span className="job-description">{job.description}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="schedule-badge">
                      {job.scheduleDescription}
                    </span>
                  </td>
                  <td className="time-cell">
                    {job.lastRunAt
                      ? formatDistanceToNow(new Date(job.lastRunAt), { addSuffix: true })
                      : t('jobs.neverRun')}
                  </td>
                  <td>
                    <div className="stats-cell">
                      <span className="stat-success">
                        <CheckCircle size={12} /> {job.stats.successCount24h}
                      </span>
                      <span className="stat-failed">
                        <XCircle size={12} /> {job.stats.failureCount24h}
                      </span>
                      {job.stats.avgDurationMs && (
                        <span className="stat-duration">
                          ~{Math.round(job.stats.avgDurationMs)}ms
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="status-cell">
                      {getStatusIcon(job.stats.lastStatus)}
                      <span className={`status-text status-${job.paused ? 'paused' : job.stats.lastStatus?.toLowerCase() || 'idle'}`}>
                        {job.paused ? t('jobs.status.paused') :
                         job.stats.lastStatus ? t(`jobs.status.${job.stats.lastStatus.toLowerCase()}`) :
                         t('jobs.status.idle')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className="btn-action btn-trigger"
                        onClick={() => handleTrigger(job.jobName)}
                        disabled={triggerMutation.isPending}
                        title={t('jobs.actions.trigger')}
                      >
                        <Play size={14} />
                      </button>
                      {job.paused ? (
                        <button
                          className="btn-action btn-resume"
                          onClick={() => handleResume(job.jobName)}
                          disabled={resumeMutation.isPending}
                          title={t('jobs.actions.resume')}
                        >
                          <Play size={14} />
                        </button>
                      ) : (
                        <button
                          className="btn-action btn-pause"
                          onClick={() => handlePause(job.jobName)}
                          disabled={pauseMutation.isPending}
                          title={t('jobs.actions.pause')}
                        >
                          <Pause size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!jobs || jobs.length === 0) && (
                <tr>
                  <td colSpan={6} className="empty-state">
                    {t('jobs.noJobs')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Executions */}
      {overview && overview.recentExecutions.length > 0 && (
        <div className="section">
          <h2 className="section-title">{t('jobs.recentExecutions')}</h2>
          <div className="executions-list">
            {overview.recentExecutions.map((execution, index) => (
              <div key={index} className="execution-item">
                {getStatusIcon(execution.status)}
                <span className="execution-job">{execution.jobName}</span>
                <span className="execution-time">
                  {formatDistanceToNow(new Date(execution.startedAt), { addSuffix: true })}
                </span>
                <span className="execution-duration">{execution.durationMs}ms</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .page-container {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-icon {
    color: var(--color-brand);
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
  }

  .stat-icon.active { background: #D1FAE5; color: #047857; }
  .stat-icon.paused { background: #FEF3C7; color: #B45309; }
  .stat-icon.error { background: #FEE2E2; color: #B91C1C; }
  .stat-icon.success { background: #D1FAE5; color: #047857; }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .stat-value.error-value { color: var(--color-error, #DC2626); }
  .stat-value.success-value { color: var(--color-success, #10B981); }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  /* Section */
  .section {
    margin-bottom: 32px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
  }

  /* Table */
  .table-container {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary, #F9FAFB);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table td {
    padding: 12px 16px;
    font-size: 13px;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table tr:last-child td {
    border-bottom: none;
  }

  .data-table tr:hover {
    background: var(--color-bg-hover);
  }

  .data-table tr.row-paused {
    opacity: 0.7;
  }

  .job-name-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .job-name {
    font-weight: 500;
    font-family: monospace;
  }

  .job-description {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .schedule-badge {
    display: inline-block;
    padding: 2px 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    border-radius: 4px;
    font-size: 11px;
    font-family: monospace;
  }

  .time-cell {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .stats-cell {
    display: flex;
    gap: 12px;
    font-size: 12px;
  }

  .stat-success {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--color-success, #10B981);
  }

  .stat-failed {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--color-error, #DC2626);
  }

  .stat-duration {
    color: var(--color-text-muted);
    font-family: monospace;
  }

  .status-cell {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .status-icon {
    width: 16px;
    height: 16px;
  }

  .status-icon.status-success { color: var(--color-success, #10B981); }
  .status-icon.status-failed { color: var(--color-error, #DC2626); }
  .status-icon.status-running { color: var(--color-brand); }
  .status-icon.status-skipped { color: var(--color-warning, #F59E0B); }
  .status-icon.status-idle { color: var(--color-text-muted); }

  .status-text {
    font-size: 12px;
  }

  .status-text.status-paused { color: var(--color-warning, #F59E0B); }
  .status-text.status-success { color: var(--color-success, #10B981); }
  .status-text.status-failed { color: var(--color-error, #DC2626); }

  .actions-cell {
    display: flex;
    gap: 8px;
  }

  .btn-action {
    width: 28px;
    height: 28px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-action:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .btn-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-trigger { color: var(--color-success, #10B981); }
  .btn-pause { color: var(--color-warning, #F59E0B); }
  .btn-resume { color: var(--color-success, #10B981); }

  .empty-state {
    text-align: center;
    color: var(--color-text-muted);
    padding: 40px 16px !important;
  }

  /* Recent Executions */
  .executions-list {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .execution-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .execution-item:last-child {
    border-bottom: none;
  }

  .execution-job {
    flex: 1;
    font-family: monospace;
    font-size: 13px;
  }

  .execution-time {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  .execution-duration {
    font-size: 12px;
    font-family: monospace;
    color: var(--color-text-muted);
  }

  /* Loading/Error States */
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: var(--color-text-secondary);
  }

  .error-state {
    color: var(--color-error, #DC2626);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @media (max-width: 768px) {
    .page-container {
      padding: 16px;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

export default JobDashboardPage;
